/* eslint-disable max-len */
import { differenceInMinutes } from 'date-fns';
import {
  DIGITAL_DISPATCH_NETWORK_TOWMAGIC,
  DIGITAL_DISPATCH_NETWORK_TOWBOOK,
  DIGITAL_DISPATCH_NETWORK_ANALOG,
  PROVIDER_RESULTS_DIGIAL_APPLICATION_NAME_TO_NORMALIZED_DIGITAL_APPLICATION_NAME
} from 'src/constants/digitalProviderNetwork';
import CamelCase from 'src/utils/CamelCase';
import { PROVIDER_SEARCH_DEFAULT_PAGE_SIZE } from 'src/utils/providerSearchConstants';
import axiosInstances from '../utils/axiosInstances';
import TowMagicService from './TowMagicService';
import TowBookService from './TowBookService';
import GoogleAddressParser from '../utils/googleAddressParser';

/**
 * @typedef {object} ContractorInfo
 * @property {string} providerId - Motor club contractorId in DigitalDispatch system
 * @property {string} name - Company Name
 * @property {string} address - Company Address
 * @property {string} city - Company City
 * @property {string} state - Company State
 * @property {string} zip - Company Zip code
 * @property {string} phone - Company Phone
 * @property {string} contactEmail - Company Email
 * @property {string} contactName - Admin name
*/

/**
 * @typedef {object} NewContractorInfo
 * @property {string} businessName - Company Name
 * @property {string} dispatchEmail - Company Email
 * @property {string} address - Company Address
 * @property {string} city - Company City
 * @property {string} state - Company State
 * @property {string} country - Company Country
 * @property {string} zip - Company Zip code
 * @property {number} latitude - Company Latitude
 * @property {string} longitude - Company Longitude
 * @property {string} googleLocationId - Company Google Place Id
*/

/**
 * @typedef {object} PaymentInformation
 * @property {number} suggestedRetailBaseCostThreshold
 * @property {number} suggestedRetailBaseCost
 * @property {number} amount
 * @property {boolean} requiresCallSupervisorApproval
 * @property {boolean} requiresWexCardToBeGenerated
 * @property {string} createdByEmail
 * @property {number} createdByAssetManagementId
 */

/**
 * @typedef {object} DispatchAddress
 * @property {string} address
 * @property {string} fullAddress
 * @property {number} addressTypeId
 * @property {string} city
 * @property {number} addressId
 * @property {string} googleLocationId
 * @property {string} latitude
 * @property {string} longitude
 * @property {string} postalCode
 * @property {string} stateProvidence
 */

/**
 * @typedef {object} QuoteDetail
 * @param {number} total
 * @param {number} tax
 * @param {number} subtotal
 */

/**
 * @typedef {Object} AgentRole
 * @property {number} id - Role ID
 * @property {string} name - Role name
 * @property {string} [createdAt] - Role creation date
 * @property {string} [updatedAt] - Role update date
 */

/**
 * @typedef {Object} Agent
 * @property {string} id - Agent Azure ID
 * @property {string} email - Agent email
 * @property {string} firstName - Agent first name
 * @property {string} lastName - Agent last name
 * @property {string} fullName - Agent full name
 * @property {string} createdAt - Agent creation date
 * @property {string} updatedAt - Agent update date
 * @property {AgentRole[]} roles - Array of agent roles
 * @property {string} [tenantId] - Tenant ID
 * @property {number} [assetManagementRecordId] - Asset management record ID
 */

/**
 * @typedef {import('axios').AxiosResponse} AxiosResponse
 * @typedef {import('src/services/DigitalDispatchWebPubSubService')} DigitalDispatchWebPubSubService
 */

class DigitalDispatchService {
  /**
   * @type {Axios}
   */
  #dispatchQoreAxiosInstance;

  /**
   * @type {Axios}
   */
  #providerAxiosInstance;

  constructor() {
    this.#dispatchQoreAxiosInstance = axiosInstances({
      baseURL: process.env.REACT_APP_DISPATCH_QORE_API,
      headers: {
        'Ocp-Apim-Subscription-Key': process.env.REACT_APP_NSD_API_KEY
      },
      withCredentials: true
    });

    this.#providerAxiosInstance = axiosInstances({
      baseURL: process.env.REACT_APP_PROVIDER_API,
      headers: {
        'Ocp-Apim-Subscription-Key': process.env.REACT_APP_NSD_API_KEY
      },
      withCredentials: true
    });

    /** Set up Axios Interceptor on other routes to ensure data comes back camel-cased */
    this.#providerAxiosInstance.interceptors.response.use((response) => {
      const camelCasedResponseData = CamelCase(response.data);
      response.data = camelCasedResponseData;
      return response;
    });
  }

  /**
   * @param {{
   * postApprovalEventRecordId: number
   * dispatchId: string
   * providerId: string
   * motorClubShortName: string
   * [remarks]: string
   * updateType: string
   * [notes]: string
   * approved: boolean
   * updatedEta: number
   * [responseCode]: string
   * digitalProviderNetwork: string
   * }} values
   * @param {boolean} dispatchHasProviderPayment
   * @param {Object} dispatch
   * @returns {Promise<AxiosResponse>}
  */
  async sendPostApprovalEventResponse(
    values,
    dispatchHasProviderPayment,
    dispatch
  ) {
    let postApprovalEventResponse;

    switch (values.digitalProviderNetwork) {
      case DIGITAL_DISPATCH_NETWORK_TOWMAGIC:
        postApprovalEventResponse = TowMagicService.mapPostApprovalEventResponsePayload(values);
        break;
      case DIGITAL_DISPATCH_NETWORK_TOWBOOK:
        postApprovalEventResponse = TowBookService.mapPostApprovalEventResponsePayload(values);
        break;
      default:
        postApprovalEventResponse = values;
    }

    const filteredObj = Object.fromEntries(
      Object.entries(postApprovalEventResponse).filter(([_, value]) => value !== '')
    );
    return this.#dispatchQoreAxiosInstance.post('/respond-to-post-approval-events-digital-dispatch', {
      ...filteredObj,
      dispatchHasProviderPayment,
      dispatch
    });
  }

  /**
   * @param {ContractorInfo} contractors
   * Takes in a list of selected contractors and maps out the contractor info
   * to fit inside of sendOfferToProviders payload
   * @returns {array}
   */

  static parsedSelectedContractors(contractors) {
    return contractors.map((contractor) => (
      {
        // For TowBook their providers are identified by TowId
        // so we send in that value as providerId
        providerId: PROVIDER_RESULTS_DIGIAL_APPLICATION_NAME_TO_NORMALIZED_DIGITAL_APPLICATION_NAME[contractor.digitalProviderNetwork]
          === DIGITAL_DISPATCH_NETWORK_TOWBOOK ? contractor.towId : contractor.providerId,
        name: contractor.businessName,
        address: contractor.address,
        city: contractor.city,
        state: contractor.stateProvince,
        zip: contractor.postalCode,
        phone: contractor.telephoneNumber,
        contactEmail: contractor.dispatchEmail || contractor.businessEmail,
        contactName: contractor.businessName,
        providerCategory: contractor.providerCategory,
        providerHoursOfOperation: contractor.hoursOfOperation,
        totalBaseRate: contractor.totalBaseRate,
        serviceRate: contractor.serviceRate,
        loadedMileageRate: contractor.loadedMileageRate,
        freeLoadedMiles: contractor.freeLoadedMiles,
        unloadedMileageRate: contractor.unloadedMileageRate,
        freeUnloadedMiles: contractor.freeUnloadedMiles,
        additionalRate: contractor.additionalRate,
        surchargeRate: contractor.surchargeRate,
        goaRate: contractor.goaRate,
        includedDents: contractor.includedDents,
        additionalDentRate: contractor.additionalDentRate,
        proxMileDistance: contractor.proxMileDistance,
        digitalProviderNetwork: contractor.digitalProviderNetwork,
        towId: contractor.towId,
        paymentInformation: contractor.paymentInformation || null,
      }
    ));
  }

  /**
   * Send a dispatch to a list of providers using their internal Digital Dispatch
   * @param {Object} dispatch
   * @param {ContractorInfo[]} contractors -
   * List of Digital Dispatch Provider/Contractor objects with
   * provider ids
   * @returns {Promise<Object[]>} - List of objects with HTTP
   * status codes (200, 400 , 404), status message (if applicable),
   * contractor name, and contractorInfo object. (There is no response body to parse for a 200,
   * the 4xx status codes do return a message)
   */
  async sendOfferToProviders(
    dispatch,
    selectedContractors,
    offerCreatedByAzureId,
    offerCreatedByEmail,
  ) {
    const parsedContractors = DigitalDispatchService.parsedSelectedContractors(selectedContractors);
    const { data: offerSubmissionResponses } = await this.#dispatchQoreAxiosInstance.post('/offer-submission-digital-dispatch', {
      dispatch,
      selectedContractors: parsedContractors,
      offerCreatedByAzureId,
      offerCreatedByEmail
    });
    return offerSubmissionResponses;
  }

  /**
   * Get the difference in minutes between the dateCreated for
   * the dispatch and preferred client pickup time
   * @param {string} preferredPickupTime
   * @param {string} dispatchCreationTime
   * @returns {number}
   */
  static getPreferredEta(preferredPickupTime, dispatchCreationTime) {
    const startDate = new Date(dispatchCreationTime);
    const expectedPickupDate = new Date(preferredPickupTime);
    return differenceInMinutes(expectedPickupDate, startDate);
  }

  /**
   * Retrieves the all dispatch status updates associated with one dispatch
   * @param {number} dispatchId
   * @returns {Promise<AxiosResponse>}
   */
  async getDigitalDispatchDispatchStatus(dispatchId) {
    return this.#dispatchQoreAxiosInstance.get(`/get-dispatch-status-updates-digital-dispatch/${dispatchId}`);
  }

  /**
   * Retrieves the all post approval events associated with one Digtal Dispatch
   * dispatch
   * @param {number} dispatchId
   * @returns {Promise<AxiosResponse>}
   */
  async getDigitalDispatchPostApprovalEvents(dispatchId) {
    return this.#dispatchQoreAxiosInstance.get(`/get-post-approval-events-digital-dispatch/${dispatchId}`);
  }

  /**
   * Retrieves all valid (not from a provider that cancelled or is not currently assigned)
   * post approval events from active Digital Dispatches
   * @returns {Promise<AxiosResponse>}
   */
  async getDigitalDispatchPostApprovalEventsThatNeedResponses() {
    return this.#dispatchQoreAxiosInstance.get('/get-post-approval-events-unanswered-digital-dispatch');
  }

  /** Retrieves the all location status updates associated with one dispatch
   * @param {number} dispatchId
   * @returns {Promise<AxiosResponse>}
   */
  async getDigitalDispatchLocationUpdates(dispatchId) {
    return this.#dispatchQoreAxiosInstance.get(`/get-location-status-updates-digital-dispatch/${dispatchId}`);
  }

  /**
   * Fetches records from the logging table [dispatch].[DigitalDispatchSentProviderOfferLogs]
   * which tracks which providers this particular dispatch was already sent to
   * @param {number} dispatchId
   * @return {Promise<AxiosResponse>}
   */
  async getDigitalDispatchSentProviderOffers(dispatchId) {
    return this.#dispatchQoreAxiosInstance.get(`/get-sent-provider-offers-digital-dispatch/${dispatchId}`);
  }

  /**
   * Cancels tow magic job offer. Sets canBeRedispatched on related row
   * dispatch
   * @param {number} dispatchId
   * @param {object} providersToCancelOffersFor
   * @param {string} remarks - Any comments and remarks entered on cancel dispatch modal
   * @param {number} dispatchStateId
   * @param {bool} dispatchHasRetailPayment
   * @param {bool} dispatchHasProviderPayment
   * @param {bool|null} isDigitalDispatch
   * @param {number} towNetId
   * @param {string} canceledByUserAzureId
   * @param {string} canceledByUserEmail
   * @returns {Promise<AxiosResponse>}
   */
  cancelDispatchAndRelatedEntities(
    dispatch,
    providersToCancelOffersFor,
    remarks,
    dispatchStateId,
    dispatchHasRetailPayment,
    dispatchHasProviderPayment,
    isDigitalDispatch,
    towNetId,
    canceledByUserAzureId,
    canceledByUserEmail
  ) {
    return this.#dispatchQoreAxiosInstance.post('/cancelDispatchAndRelatedEntities', {
      dispatch,
      providersToCancelOffersFor,
      remarks,
      dispatchStateId,
      dispatchHasRetailPayment,
      dispatchHasProviderPayment,
      isDigitalDispatch: isDigitalDispatch || null,
      towNetId,
      canceledByUserAzureId,
      canceledByUserEmail
    });
  }

  /**
   * Cancels tow magic job offer. Sets canBeRedispatched on related row
   * dispatch
   * @param {number} dispatchId
   * @param {object} providersToCancelOffersFor
   * @param {string} remarks - Any comments and remarks entered on cancel dispatch modal
   * @param {number} dispatchStateId
   * @param {bool} dispatchHasRetailPayment
   * @param {bool} dispatchHasProviderPayment
   * @param {bool|null} isDigitalDispatch
   * @param {number} towNetId
   * @returns {Promise<AxiosResponse>}
   */
  sendRequestToCancelCompletedDispatch(
    dispatchId,
    providerId,
    cancelRemarks,
  ) {
    return this.#dispatchQoreAxiosInstance.post('/cancel-completed-dispatch-pending', {
      dispatchId: Number(dispatchId),
      providerId: Number(providerId),
      cancelRemarks,
    });
  }

  /**
   * Reschedules a digital dispatch that does not have a provider assigned yet
   * @param {*} dispatchId
   * @param {*} newClientPickupTime
   * @param {*} isAutoDispatch
   * @returns {Promise<AxiosResponse>}
   */
  rescheduleDispatchNoProvider(
    dispatchId,
    newClientPickUpTime,
    isAutoDispatch
  ) {
    return this.#dispatchQoreAxiosInstance.post('/reschedule-dispatch-no-provider', {
      dispatchId: Number(dispatchId),
      newClientPickUpTime,
      isAutoDispatch
    });
  }

  /**
   * Fetches the dispatch status of a particular Qore dispatch;
   * this endpoint returns whether or not a dispatch is cancelled
   * and if is it is allowed to be redispatched
   * @param {number} dispatchId
   * @returns {Promise<AxiosResponse>}
   */
  getQoreDispatchCancelStatus(dispatchId) {
    return this.#dispatchQoreAxiosInstance.get(`/get-dispatch-cancel-status/${dispatchId}`);
  }

  /**
 * Retrieves providers by priority using the provider algorithm SPROC.
 * @param {Object} params
 * @param {number} params.latitude
 * @param {number} params.longitude
 * @param {number} params.radius
 * @param {string} params.zip
 * @param {string} params.serviceType
 * @param {string} params.dispatchId
 * @param {[string]| string |null} [params.filterBy = null]
 * @param {number} [params.page = 1]
 * @param {number} [parmas.pageSize]
 * @param {number} [params.producerCode]
 * @param {number} [params.loadedMiles]
 * @returns {Promise<AxiosResponse>}
 */
  getProvidersByPriority({
    latitude,
    longitude,
    radius,
    zip,
    serviceType,
    dispatchId,
    producerCode,
    loadedMiles,
    filterBy = null,
    page = 1,
    pageSize = PROVIDER_SEARCH_DEFAULT_PAGE_SIZE
  }) {
    return this.#providerAxiosInstance.post('/provider-search', {
      latitude,
      longitude,
      radius,
      zip,
      serviceType,
      dispatchId,
      filterBy,
      page,
      pageSize,
      producerCode,
      loadedMiles,
    });
  }

  /**
   * Retrieves the business address for a provider in
   * the same format as the pickup and dropoff address
   * for a dispatch.
   * @param {number} towId
   * @returns {Promise<AxiosResponse<DispatchAddress>>}
   */
  getProviderAddress(towId) {
    return this.#providerAxiosInstance.get(`/get-provider-address/${towId}`);
  }

  /**
   * Assigns analog provider to dispatch
   * @param {Object} dispatch
   * @param {ContractorInfo[]} selectedProvider
   * @param {PaymentInformation} paymentInformation
   * @returns {Promise<AxiosResponse>}
   */

  assignAnalogProvider(
    {
      dispatchDetails,
      selectedProvider,
      paymentInformation
    }
  ) {
    return this.#dispatchQoreAxiosInstance.post('/assign-analog-provider', {
      dispatch: dispatchDetails,
      selectedProvider: {
        ...selectedProvider,
        digitalProviderNetwork: DIGITAL_DISPATCH_NETWORK_ANALOG
      },
      paymentInformation
    });
  }

  /**
   * Logs analog provider denial offer
   * @param {Object} dispatch
   * @param {ContractorInfo[]} selectedProvider
   * @param {Boolean} isOfferTimeout
   * @param {PaymentInformation} paymentInformation
   * @returns {Promise<AxiosResponse>}
   */

  logAnalogProviderDenialOffer(
    {
      dispatchDetails,
      selectedProvider,
      isOfferTimeout,
      paymentInformation
    }
  ) {
    return this.#dispatchQoreAxiosInstance.post('/analog-provider-offer-denial', {
      dispatch: dispatchDetails,
      selectedProvider,
      isOfferTimeout,
      paymentInformation
    });
  }

  /**
 * Logs analog provider offer as pending because the estimated quote from the agent recevied
 * was 30% above the estimated cost (only for OON analog providers)
 * @param {Object} dispatch
 * @param {ContractorInfo[]} selectedProvider
 * @param {PaymentInformation} paymentInformation
 * @returns {Promise<AxiosResponse>}
 */
  logAnalogProviderPendingOffer(
    {
      dispatchDetails,
      selectedProvider,
      paymentInformation
    }
  ) {
    return this.#dispatchQoreAxiosInstance.post('/analog-provider-offer-pending', {
      dispatch: dispatchDetails,
      selectedProvider,
      paymentInformation
    });
  }

  /**
   * @param {Object} provider
   * Takes in provider with data from Google Places API and
   * shapes that data into an object we can use in our analog provider form.
   * API Doc for Place Details https://developers.google.com/maps/documentation/places/web-service/details
   * @returns {ContractorInfo}
   */

  static parsedOutOfNetworkProviderDetails(provider) {
    const googleAddressParser = new GoogleAddressParser();
    const addressDescription = provider?.formatted_address?.split(' ')[0];

    googleAddressParser.parseAddress(
      provider?.formatted_address,
      provider?.address_components,
      addressDescription
    );
    const {
      city,
      address,
      stateProvidence,
      country,
      postalCode
    } = googleAddressParser.toJson();

    const openingHours = provider?.opening_hours?.weekday_text
      ? `${provider?.opening_hours?.weekday_text}`.replace(/[\[|\]'"`]/g, ' ') : '';

    const telephoneNumber = provider?.formatted_phone_number ? provider?.formatted_phone_number.replace(/\D/g, '') : '';

    return {
      address,
      city,
      stateProvince: stateProvidence,
      country,
      zip: postalCode,
      latitude: provider?.geometry?.location?.lat,
      longitude: provider?.geometry?.location?.lng,
      telephoneNumber,
      hoursOfOperation: openingHours
    };
  }

  /**
   * Retrieves OON State Retail Costs that are associed with the dispatch servicec key.
   * @param {String} stateCode
   * @param {String} serviceKey
   * @returns {Promise<Object>} -
   */

  fetchOutOfNetworkStateRetailCost(stateCode, serviceKey) {
    return this.#providerAxiosInstance.get(`/get-Out-Of-Network-State-Retail-Cost/${stateCode}/${serviceKey}`);
  }

  /**
   * Retrieves out of network provider information from Google Places API and
   * using placeId to find that provider.
   * API Doc for Place Details https://developers.google.com/maps/documentation/places/web-service/details
   * @param {String} placeId
   * @returns {Promise<ContractorInfo>} -
   */

  async fetchOutOfNetworkProviderDetails(placeId) {
    const response = await this.#dispatchQoreAxiosInstance.get(`/place-details?placeId=${placeId}`);

    return DigitalDispatchService.parsedOutOfNetworkProviderDetails(response.data.result);
  }

  /**
   * Retrieves Google Maps API place details for a location given a pair of lat and long coordinates
   * @param {string} lat
   * @param {string} long
   */
  getPlaceDetailsFromLatLong(lat, long) {
    return this.#dispatchQoreAxiosInstance.get(`place-details?latlng=${lat},${long}`);
  }

  /**
   * When a new out of network provider is selected we need to insert that new  provider
   * in our provider table and assign that new provider to the current dispatch.
   *
   * NOTE: This is mapping totalBaseRate to ServiceRate for new OON providers for the time-being
   * This is a hotfix for DD2-1904, and is ONLY for new OON providers to ensure that we can process
   * the dispatch where we no longer collect a service rate in the Analog Provider form
   * @param {NewContractorInfo} newProvider
   * @returns {Promise<ContractorInfo>}
   */

  async insertNewOutOfNetworkProvider({
    businessName,
    businessEmail,
    dispatchEmail,
    contactName,
    dispatcherPhone,
    proxMileDistance,
    providerCategory,
    digitalProviderNetwork,
    address,
    city,
    stateProvince,
    zip,
    country,
    telephoneNumber,
    latitude,
    longitude,
    googleLocationId,
    hoursOfOperation,
    estimatedPrice,
    providerEta,
    providerNotes,
    serviceKey,
    isTowService,
    employerIN,
    totalBaseRate,
    loadedMileageRate = 0,
    freeLoadedMiles = 0,
    unloadedMileageRate = 0,
    freeUnloadedMiles = 0,
    additionalRate = 0,
    surchargeRate = 0,
    goaRate = 0,
    includedDents = 0,
    additionalDentRate = 0,
    telephoneType = 'Business',
    addressType = 'Business',
  }) {
    const response = await this.#providerAxiosInstance.post('/insert-new-out-of-network-provider', {
      businessName,
      businessEmail: businessEmail || dispatchEmail,
      dispatchEmail,
      address,
      city,
      state: stateProvince,
      zip,
      country,
      latitude,
      longitude,
      googleLocationId,
      employerIN,
      serviceRate: totalBaseRate, // TODO: need a long-term fix here.  This was added for DD2-1904
      addressType,
      telephoneNumber,
      serviceKey
    });
    return {
      providerId: response.data.providerId,
      businessName: response.data.businessName,
      businessEmail: response.data.businessEmail,
      dispatchEmail: response.data.dispatchEmail,
      address: response.data.address,
      addressType,
      city: response.data.city,
      stateProvince: response.data.state,
      postalCode: response.data.zip,
      country: response.data.country,
      employerIN,
      serviceRate: totalBaseRate, // TODO: need a long-term fix here.  This was added for DD2-1904
      contactName,
      telephoneNumber,
      telephoneType,
      dispatcherPhone,
      proxMileDistance,
      providerCategory,
      digitalProviderNetwork,
      hoursOfOperation,
      estimatedPrice,
      providerEta,
      providerNotes,
      serviceKey,
      isTowService,
      loadedMileageRate,
      freeLoadedMiles,
      unloadedMileageRate,
      freeUnloadedMiles,
      additionalRate,
      surchargeRate,
      goaRate,
      includedDents,
      additionalDentRate,
      totalBaseRate,
      towId: response.data.towId,
    };
  }

  /**
   * Method used for updating the dispatch status and state in Dispatch.Dispatch for
   * analog providers assigned using the new digital integration. Only the string value
   * for the status is passed since there is a mapping for status and state on the API endpoint
   * @param {number} dispatchId
   * @param {'Accepted' | 'In Route' | 'On Scene' | 'New' | 'Tow In Progress' | 'Destination Arrival' | 'Complete' | 'Cancelled' } status
   * @param {string} email - CSR/user responsible for the update
   * @returns {Promise<AxiosResponse>}
   */
  async submitStatusAndStateChangeForAnalogProvider(
    dispatchId,
    status,
    email,
    state = null,
    secondaryDataId = null,
    secondaryCompleteData = null,
    isValidated,
    userAzureId,
    auditDescription
  ) {
    return this.#dispatchQoreAxiosInstance.post('/update-dispatch-status-state', {
      status,
      dispatchId,
      email,
      state,
      secondaryDataId,
      secondaryCompleteData,
      validation: isValidated,
      userAzureId,
      auditDescription
    });
  }

  /**
   * @param {object} payload
   * @property {number} payload.dispatchId
   * @property {DispatchAddress} payload.dropOffLocation
   * @property {DispatchAddress} payload.pickUpLocation
   * @property {number} payload.updatedDistance
   * @property {number} payload.isDigitalDispatch
   * @property {number} payload.stateId
   * @property {number} payload.statusId
   * @property {number} payload.vehicleLatitude
   * @property {number} payload.vehicleLongitude
   * @property {number} payload.overageRetailPaymentAmount
   * @property {QuoteDetail} payload.priceDetails
   * @returns {Promise<AxiosResponse>}
   */
  updateDispatchLocation(payload) {
    return this.#dispatchQoreAxiosInstance.post('/update-dispatch-location', payload);
  }

  /**
   * @param {number} towNetId
   * @param {number} producerCode
   * @returns {Promise<AxiosResponse>}
   */
  doesDispatchHaveProviderPayment(towNetId, producerCode) {
    return this.#dispatchQoreAxiosInstance.post('/doesDispatchHaveProviderPayment', { towNetId, producerCode });
  }

  /**
   * Retreives details from Auto Dispatch
   * @param {String} dispatchId
   * @returns {Promise<Object>} -
   */
  async fetchAutoDispatchDetails(dispatchId) {
    const response = await this.#dispatchQoreAxiosInstance.get(`/get-auto-dispatch-details/${dispatchId}`);
    return response.data;
  }

  /**
   * Updates auto dispatch details
   * @param {number} dispatchId
   * @param {number} statusId
   * @param {string} endTime
   * @param {number} currentProviderId,
   * @param {number} nextProviderId
   * @returns {Promise<AxiosResponse>}
   */

  updateAutoDispatchDetails(updatedAutoDispatchDetails) {
    return this.#dispatchQoreAxiosInstance.post('/update-auto-dispatch-details', { ...updatedAutoDispatchDetails });
  }

  /**
   * Updates auto dispatch details
   * @param {number} dispatchId
   * @param {number} statusId
   * @param {string} endTime
   * @param {number} currentProviderId,
   * @param {number} nextProviderId
   * @returns {Promise<AxiosResponse>}
   */

  sendCustomerUpdateMessage(updateMessageDetails) {
    return this.#dispatchQoreAxiosInstance.post('/sendCustomerUpdateMessage', { ...updateMessageDetails });
  }

  /**
   * Fetches the dispatch status log of a particular Qore dispatch;
   * this endpoint returns the users and date stamp when a dispatch is transitioned to accepted/rejected/manager approved
   * @param {number} dispatchId
   * @returns {Promise<AxiosResponse>}

 */
  getDispatchStatusLog(dispatchId) {
    return this.#dispatchQoreAxiosInstance.get(`/get-dispatch-status-updatedby-log/${dispatchId}`);
  }

  /**
   * Fetches the CSR assigned to a dispatch and the number of dispatches assigned to each CSR;
   * this endpoint returns the csrName, and number of dispatches assigned to that CSR
   * @returns {Promise<AxiosResponse>}

 */
  getAssignedDispatchAgents() {
    return this.#dispatchQoreAxiosInstance.get('/get-assigned-dispatch-agents');
  }

  /**
   * Fetches the active purchase order of a particular Qore dispatch;
   * this endpoint returns the PO record
   * @param {number} dispatchId
   * @returns {Promise<AxiosResponse>}

 */
  getDispatchActivePurchaseOrder(dispatchId) {
    return this.#dispatchQoreAxiosInstance.get(`/get-active-purchase-order?dispatchId=${dispatchId}`);
  }

  /**
   * Gets member coverage eligibility status and an array of dispatches
   * to be rendered on the UI if the policy's service limit has been
   * exceeded.
   * @param     {object}  params
   * @property  {string}  [params.memberNumber]
   * @property  {string}  [params.vin]
   * @property  {string}  [params.effectiveDate]
   * @property  {string}  [params.serviceKey]
   * @property  {boolean} [params.runAllCalls]
   * @property  {number}  [params.servicesPerYear]
   * @property  {number}  [params.servicesPerHour]
   * @property  {number}  [params.servicesPerHourForSameVehicle]
   * @property  {number}  [params.servicesPerHourForSameVehicleDifferentService]
   * @property  {number}  [params.servicesPerHourForDifferentVehicleSameService]
   * @property  {number}  [params.servicesPerContract]
   * @returns   {Promise<AxiosResponse>}
   */
  getMemberCoverageEligibility(params) {
    return this.#dispatchQoreAxiosInstance.post('/get-member-coverage-eligibility', params);
  }

  /**
   * Assigns an agent to an out of network dispatch
   * @param {number} dispatchId
   * @param {Agent} agent
   * @returns {Promise<AxiosResponse>}
   */
  assignAgentToOutOfNetworkDispatch(dispatchId, agent) {
    return this.#dispatchQoreAxiosInstance.post('/assign-oon-dispatch-to-agent', {
      dispatchId,
      agent
    });
  }

  /**
   * Fetches the audit events for a particular dispatch;
   * @param {number} dispatchId
   * @returns {Promise<AxiosResponse>}
   */
  getDispatchAuditEvents(dispatchId) {
    return this.#dispatchQoreAxiosInstance.get(`/get-dispatch-audit-events/${dispatchId}`);
  }
}

export default DigitalDispatchService;
